import { Heading, Stack, StackProps } from '@chakra-ui/react';
import { RequireOnly } from '@pelicargo/types';
import { Show } from '@pelicargo/ui';
import {
  adminCargoTypeOptions,
  adminCargoTypeSubOptions,
  aircraftRestrictionOptions,
  batteryTypeOptions,
  cargoCoolingOptions,
  cargoCoolingRangeOptions,
  dimensionUnitOptions,
  freightServiceOptions,
  getCommodityOptions,
  getPrettyCommodity,
  kgToLb,
  modelUtils,
  packingGroupOptions,
  packingInstructionOptions,
  sectionOptions,
  serviceTypeOptions,
  TimeZone,
  weightUnitOptions,
} from '@pelicargo/utils';
import { prettyNumber } from '@pelicargo/utils';
import { CargoType } from '@prisma/client';
import { useMemo } from 'react';
import { UseFormReturn, useWatch } from 'react-hook-form';

import { useRequestForm } from '../../hooks/request/useForm';
import { useUpdateRequest } from '../../hooks/request/useUpdate';
import { useCurrentRequest } from '../../hooks/useCurrentRequest';
import { useDashboard } from '../../hooks/useDashboard';
import { Request } from '../../utils/types';
import { SimpleForm } from '../SimpleForm';
import { SimpleLabeledPopoverInput } from '../SimpleLabeledPopoverInput';
import { SimpleSelect } from '../SimpleSelect';
import { SimplePopoverUnifiedInput } from '../SimpleUnifiedPopoverInput';
import { CargoList } from './CargoList';
import { CargoOriginForm } from './CargoOriginForm';

type CargoDetailsFormProps = StackProps & {
  // The keys `CargoOriginForm` requires; everything else this form reads is optional.
  request?: RequireOnly<Request, 'id' | 'cargo_origin' | 'origin_airports' | 'freight_services'>;
};

export const CargoDetailsForm = ({ request, ...rest }: CargoDetailsFormProps) => {
  const { cargoList } = useCurrentRequest();
  const { mutate: updateRequest } = useUpdateRequest(request?.id);
  const { methods } = useRequestForm(request);
  const originTimezone = request?.origin_airports?.[0]?.time_zone as TimeZone;

  const cargoType = useWatch({ control: methods.control, name: 'cargo_type' });
  const cargoTypeSub = useWatch({ control: methods.control, name: 'cargo_type_sub' });
  const isCargoTypeTempControlled = cargoType === CargoType.Perishable;
  const commodityOptions = useMemo(() => getCommodityOptions({ cargoType, cargoTypeSub }), [cargoType, cargoTypeSub]);

  // Red flag detectors 🚩
  const knownShipperFlagged = !request?.is_known_shipper;
  const gwFlagged = request?.gross_weight > request?.volumetric_weight * 4;
  const vwFlagged = request?.volumetric_weight > request?.gross_weight * 4;
  const cwFlagged = modelUtils.cargos.mayImpactCw(cargoList);
  const isDgd = request?.cargo_type === CargoType.DangerousGoods;
  const isDgdOrBatteries = isDgd || !!request?.battery_type;
  const unFlagged = isDgd && !request?.un_number;
  const classFlagged = isDgd && !request?.class;
  const { isDevMode } = useDashboard();

  return (
    <SimpleForm onValid={updateRequest} defaultValues={request} {...(methods as UseFormReturn<any>)}>
      <Stack direction="column" spacing={4} {...rest}>
        <Stack direction="column" spacing={2}>
          <Heading fontSize="sm" ml="2">
            Service Details
          </Heading>
          <Stack direction="column" spacing={0}>
            <SimpleLabeledPopoverInput
              flex={1}
              name="dropoff_by"
              label="Expected Drop-Off"
              inputType="date-picker"
              showClearButton
              initialTimeZone={originTimezone}
              tabIndex={0}
              isHorizontal
            />
            <SimpleLabeledPopoverInput
              flex={1}
              name="freight_services"
              label="Freight Services"
              inputType="react-select"
              placeholder=""
              options={freightServiceOptions}
              isMulti
              tabIndex={0}
              isHorizontal
            />
            <SimpleLabeledPopoverInput
              flex={1}
              name="service_type"
              label="Service Type"
              inputType="react-select"
              placeholder=""
              options={serviceTypeOptions}
              maxOptionsSelected={1}
              isMulti
              tabIndex={0}
              isHorizontal
            />
            <SimpleLabeledPopoverInput
              flex={1}
              name="is_known_shipper"
              label="Known Shipper"
              inputType="checkbox"
              isHighlighted={knownShipperFlagged}
              tabIndex={0}
              isHorizontal
            />
            <SimpleLabeledPopoverInput
              flex={1}
              name="freight_status"
              label="Freight Status"
              inputType="select"
              maxOptionsSelected={1}
              options={[
                { label: 'Quoting', value: 'Quoting' },
                { label: 'Confirmed', value: 'Confirmed' },
              ]}
              tabIndex={0}
              isHorizontal
            />
            <SimpleLabeledPopoverInput
              flex={1}
              name="is_pre_screened"
              label="Pre Screened"
              inputType="checkbox"
              tabIndex={0}
              isHorizontal
            />
            <SimpleLabeledPopoverInput
              flex={1}
              name="target_rate"
              label="Target Rate"
              inputType="input"
              tabIndex={0}
              isHorizontal
            />
            <SimpleLabeledPopoverInput
              flex={1}
              name="additional_information"
              label="Additional Info"
              inputType="textarea"
              isHorizontal
            />
            <CargoOriginForm request={request} />
          </Stack>
        </Stack>
        <Stack direction="column" spacing={2}>
          <Heading fontSize="sm" ml="2">
            Cargo Details
          </Heading>
          <Stack direction="column" spacing={0}>
            <SimplePopoverUnifiedInput
              flex={1}
              label="Preferred Units"
              names={['original_weight_unit', 'original_dimension_unit']}
              joiner="/"
              tabIndex={0}
              isHorizontal
            >
              <Stack direction="row" spacing={1}>
                <SimpleSelect name="original_weight_unit" ignoreOnBlur options={weightUnitOptions} />
                <SimpleSelect name="original_dimension_unit" ignoreOnBlur options={dimensionUnitOptions} />
              </Stack>
            </SimplePopoverUnifiedInput>
            <SimpleLabeledPopoverInput
              flex={1}
              name="commodity"
              label="Commodity"
              inputType="react-select"
              options={commodityOptions}
              formatDisplayValue={getPrettyCommodity}
              tabIndex={0}
              isCreatable
              isHorizontal
            />
            <SimpleLabeledPopoverInput
              flex={1}
              name="gross_weight"
              label="GW (kg)"
              inputType="input"
              isHighlighted={gwFlagged}
              formatDisplayValue={prettyNumber}
              tabIndex={0}
              title={`${Math.round(kgToLb(request?.gross_weight ?? 0))} lb`}
              isHorizontal
            />
            <SimpleLabeledPopoverInput
              flex={1}
              name="volumetric_weight"
              label="VW (kg)"
              inputType="input"
              isHighlighted={vwFlagged}
              formatDisplayValue={prettyNumber}
              tabIndex={0}
              title={`${Math.round(kgToLb(request?.volumetric_weight ?? 0))} lb`}
              isHorizontal
            />
            <SimpleLabeledPopoverInput
              flex={1}
              name="chargeable_weight"
              label="CW (kg)"
              inputType="input"
              formatDisplayValue={prettyNumber}
              isHighlighted={cwFlagged}
              tabIndex={0}
              title={`${Math.round(kgToLb(request?.chargeable_weight ?? 0))} lb`}
              isHorizontal
            />
            <SimpleLabeledPopoverInput
              flex={1}
              name="cargo_type"
              label="Cargo Type"
              inputType="select"
              options={adminCargoTypeOptions}
              tabIndex={0}
              highlightColor="yellow"
              isHighlighted={
                request?.cargo_type === CargoType.DangerousGoods || request?.cargo_type === CargoType.Perishable
              }
              isHorizontal
            />
            <Show if={isCargoTypeTempControlled}>
              <SimpleLabeledPopoverInput
                flex={1}
                name="cargo_type_sub"
                label="Cargo Sub Type"
                inputType="select"
                options={adminCargoTypeSubOptions}
                tabIndex={0}
                isHorizontal
              />
              <SimpleLabeledPopoverInput
                flex={1}
                name="cargo_cooling_range"
                label="Temperature Range"
                inputType="select"
                options={cargoCoolingRangeOptions}
                tabIndex={0}
                isHorizontal
              />
              <SimpleLabeledPopoverInput
                flex={1}
                name="cargo_cooling"
                label="Cooling"
                inputType="select"
                options={cargoCoolingOptions}
                tabIndex={0}
                isHorizontal
              />
            </Show>
            <Show if={isDgdOrBatteries}>
              <SimpleLabeledPopoverInput
                flex={1}
                name="un_number"
                label="UN #"
                inputType="input"
                isHighlighted={unFlagged}
                isHorizontal
              />
              <SimpleLabeledPopoverInput
                flex={1}
                name="class"
                label="Class"
                inputType="number"
                isHighlighted={classFlagged}
                isHorizontal
              />
              <SimpleLabeledPopoverInput
                flex={1}
                name="packing_group"
                label="Packing Group"
                inputType="select"
                options={packingGroupOptions}
                tabIndex={0}
                isHorizontal
              />

              <SimpleLabeledPopoverInput
                flex={1}
                name="aircraft_restriction"
                label="Aircraft"
                inputType="select"
                options={aircraftRestrictionOptions}
                tabIndex={0}
                isHorizontal
              />
              <SimpleLabeledPopoverInput
                flex={1}
                name="packing_instruction"
                label="Packing Instruction"
                inputType="select"
                maxOptionsSelected={1}
                options={packingInstructionOptions}
                tabIndex={0}
                isHorizontal
              />
              <SimpleLabeledPopoverInput
                flex={1}
                name="section"
                label="Section"
                inputType="select"
                maxOptionsSelected={1}
                options={sectionOptions}
                tabIndex={0}
                isHorizontal
              />
            </Show>
            <SimpleLabeledPopoverInput
              flex={1}
              name="battery_type"
              label="Battery Type"
              inputType="select"
              maxOptionsSelected={1}
              options={batteryTypeOptions}
              tabIndex={0}
              isHorizontal
            />
            <Show if={!!request?.hs_code || isDevMode}>
              <SimpleLabeledPopoverInput label="HS Code" inputType="input" name="hs_code" tabIndex={0} isHorizontal />
            </Show>
            <Show if={request?.is_military_related !== null || isDevMode}>
              <SimpleLabeledPopoverInput
                label="Is Military"
                inputType="checkbox"
                name="is_military_related"
                tabIndex={0}
                isHorizontal
              />
            </Show>
          </Stack>
        </Stack>
        <CargoList requestId={request?.id} />
      </Stack>
    </SimpleForm>
  );
};
